export type QuestionType = 'themes' | 'dropdown' | 'checkbox-image' | 'checkbox' | 'image' | 'switch';

export interface Answer {
  id: number;
  checked: boolean;
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  theme?: string;
  products?: string[];
  content?: string[];
  pill?: string;
  result?: string;
  questions?: Question[];
}

export interface Question {
  id: number;
  title: string;
  type: QuestionType;
  answers: Answer[];
  image?: string;
  description?: string;
}

export interface ThemeAdvice { 
    theme: string; 
    advice: { products: string[]; content: string[] } 
}

export interface ThemeSelection {
  theme: Answer,
  selected: Answer[],
}

export type QuestionnaireResponseEntry = { theme: string; subthemes: Record<string, string | undefined>[] }
export type QuestionnaireResponseRecord = Record<string, QuestionnaireResponseEntry>

export class QuestionnaireService {
  public static ThemeGetQuestionCount(theme: Answer): number {
      if( theme.questions == null || theme.questions.length <= 0 ) return 0;
      return theme.questions[0].answers.length;
  }

  public static AnswerHasSubQuestions(answer: Answer): boolean {
    return answer.questions != null && answer.questions.length > 0;
  }

  public static AnswerGetSubQuestions(answer: Answer): Question[] {
    return answer.questions != null ? answer.questions : [];
  }

  public static AnswerIsCompleted(answer: Answer): boolean {
    const subQuestions: Question[] = QuestionnaireService.AnswerGetSubQuestions(answer);
    for(const question of subQuestions) {
      if( !QuestionnaireService.QuestionIsCompleted(question) ) return false;
    }
    return true;
  }

  public static AnswerGetContent(answer: Answer, defaultText: string = ""): string {
    if( answer.title && answer.title.length > 0 ) return answer.title;
    if( answer.description && answer.description.length > 0 ) return answer.description;
    return defaultText;
  }

  public static AnswerGetContentAll(answer: Answer, defaultText: string = ""): string {
    let ret: string = "";
    if( answer.title && answer.title.length > 0 ) ret = answer.title;
    if( answer.description && answer.description.length > 0 ) ret = (ret.length > 0 ? " - " : "") + answer.description;
    if( ret.length <= 0 ) return defaultText;
    return ret;
  }

  public static AnswerGetDescription(answer: Answer, defaultText: string = ""): string {
    if( answer.description && answer.description.length > 0 ) return answer.description;
    return defaultText;
  }

  public static uncheckSubAnswers(answer: Answer, uncheckRootAnser: boolean = true) {
    if( uncheckRootAnser ) answer.checked = false;
    for(const question of answer.questions ?? []) {
      for(const subAnswer of question.answers) {
        QuestionnaireService.uncheckSubAnswers(subAnswer, true);
      }
    }
  }

  public static deselectDanglingSelection(question: Question, parentIsSelected: boolean = true) {
    for(const answer of question.answers) {
      if( !parentIsSelected ) { 
        console.log("unchecking " + answer.id);
        answer.checked = false;
      }
      for(const subQuestion of answer.questions ?? []) {
        this.deselectDanglingSelection(subQuestion, answer.checked);
      }
    }
  }

  public static QuestionHasSubQuestions(question: Question): boolean {
    for(const answer of question.answers) {
      if( QuestionnaireService.AnswerHasSubQuestions(answer) ) return true;
    }
    return false;
  }

  public static QuestionIsCompleted(question: Question|null, checkSubQuestions: boolean = true): boolean {
    if( question == null ) return true;
    let checkedAnswers: number = 0;
    for(const answer of question.answers) {
      if( answer.checked ) {
        checkedAnswers++;
        if( checkSubQuestions ) {
          for(const subQuestion of QuestionnaireService.AnswerGetSubQuestions(answer)) {
            if( !QuestionnaireService.QuestionIsCompleted(subQuestion) ) return false;
          }
        }
      }
    }
    return checkedAnswers > 0;
  }

  public static getCheckedAnswers(answers: Answer[], currentlyChecked: Answer[]|null = null): Answer[] {
    currentlyChecked = currentlyChecked ?? [];
    for(const answer of answers) {
      if( answer.checked ) {
        currentlyChecked.push(answer)
      }
      for(const question of answer.questions ?? []) {
        QuestionnaireService.getCheckedAnswers(question.answers, currentlyChecked);
      }
    }
    return currentlyChecked;
  }

  public static getCheckedAnswersByTheme(questionnaire: Question, completedThemes: {[key: number]: Answer} = {}): ThemeSelection[] {
    const ret: ThemeSelection[] = [];
    for(const theme of questionnaire.answers) {
      if( (!completedThemes[theme.id] && !theme.checked) || theme.questions == null || theme.questions.length <= 0 ) continue;
      
      ret.push({
        theme: theme,
        selected: QuestionnaireService.getCheckedAnswers(theme.questions[0].answers),
      })
    }
    return ret;
  }

  public static createQuestionnaireSubmission(questionnaire: Question, selectedThemes: Answer[], trackEvent: (eventName: string, data: Record<string, unknown>) => void) {
    const results: Question = { ...questionnaire };
    // No longer needed. The create response method stops when an answer isn't selected
    // QuestionnaireService.deselectDanglingSelection(results);
    const response: ThemeAdvice[] = [];
    QuestionnaireService.createResponse(results, response);
    trackEvent('Click_RequestAdvice', {});
    const groupedData: QuestionnaireResponseRecord = QuestionnaireService.getCheckedAnswers(selectedThemes).reduce<QuestionnaireResponseRecord>((acc, item) => {
      if (!item.theme) return acc;
      if (!acc[item.theme]) acc[item.theme] = { theme: item.theme, subthemes: [] };
      const title = String(item.id);
      const subtheme = acc[item.theme].subthemes.find((sub) => sub[title] !== undefined);
      if (subtheme) {
        subtheme[title] = item.result;
      } else {
        acc[item.theme].subthemes.push({ [title]: item.result });
      }
      return acc;
    }, {});
    const encodeResponseToParams = (response: ThemeAdvice[]) =>
      response.map((item, index) => {
        const themeParam = `theme${index}=${encodeURIComponent(item.theme)}`;
        const productsParam = `products${index}=${encodeURIComponent(item.advice.products.join(','))}`;
        const contentParam = `content${index}=${encodeURIComponent(item.advice.content.join(','))}`;
        return `${themeParam}&${productsParam}&${contentParam}`;
      }).join('&');
    const urlParams = encodeResponseToParams(response);

    return {
      groupedData: groupedData,
      response: response,
      urlParams: urlParams,
    };
  }

  protected static createResponse = (question: Question | null | undefined, response: ThemeAdvice[]) => {
    if( !question || !question.answers ) return;

    question.answers.forEach((answer) => {
      // Change: skip when the answer is not checked. Do not process the underlying questions
      // this means we no longer have to recursively disable dangling selections before submitting
      if( !answer.checked ) return;

      if(
        ((answer.products && answer.products.length > 0) || (answer.content && answer.content.length > 0)) &&
        answer.theme !== null
      ) {
        let existingTheme = response.find((item) => item.theme === answer.theme);
        if (!existingTheme) {
          existingTheme = { theme: answer.theme!, advice: { products: [], content: [] } };
          response.push(existingTheme);
        }
        if (answer.products) {
          existingTheme.advice.products = [...new Set([...existingTheme.advice.products, ...answer.products])];
        }
        if (answer.content) {
          existingTheme.advice.content = [...new Set([...existingTheme.advice.content, ...answer.content])];
        }
      }
      if (answer.questions) {
        answer.questions.forEach((subQuestion) => QuestionnaireService.createResponse(subQuestion, response));
      }
    });
  };
}